import { Button, Col, message, Row } from 'antd';
import config from 'config';
import { format } from 'date-fns';
import { useGetIneligibleParticipantsReportsLazyQuery } from 'gql/types';
import { useChunkedLoader } from 'modules/common/hooks/useChunkedLoader';
import { useReportFilterOptions } from 'modules/reports/hooks/useReportFilterOptions';
import { useReportFilters } from 'modules/reports/hooks/useReportFilters';
import { generateReportHeaders } from 'modules/reports/utils/generateReportHeaders';
import { mapReportData } from 'modules/reports/utils/mapReportData';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getIneligibleReportColumns } from './columns';
import { getIneligibleReportFilters } from './filters';
import { transformIneligibleData } from './utils/transformIneligibleData';

import { ReportDownloads } from '../shared/ReportDownloads';
import { ReportFilterBar } from '../shared/ReportFilterBar';

const IneligibleReports = () => {
  const { t } = useTranslation('reports.IneligiblesReports');

  const fileName = `ineligible_participants_${format(
    new Date(),
    config.DATE_FORMATS.SHORT_DATE
  )}`;

  const [getIneligibleReports] = useGetIneligibleParticipantsReportsLazyQuery({
    fetchPolicy: 'network-only',
  });

  const {
    isLoading,
    data: reports,
    load: loadReports,
  } = useChunkedLoader({
    query: async (input) => {
      const result = await getIneligibleReports({ variables: { input } });
      if (result.error) {
        throw Error(result.error.message);
      }
      return {
        meta: result.data?.getIneligibleParticipantsReports.meta,
        data: transformIneligibleData(
          result.data?.getIneligibleParticipantsReports.data ?? []
        ),
      };
    },
    onError: (error) => message.error(error.message),
    onEmpty: () => message.info(t('emptyReport')),
  });

  const columns = useMemo(() => getIneligibleReportColumns(t), [t]);
  const headers = useMemo(() => generateReportHeaders(columns), [columns]);
  const filterOptions = useReportFilterOptions();
  const filterConfigs = useMemo(
    () => getIneligibleReportFilters(t, filterOptions),
    [t, filterOptions]
  );

  const {
    values,
    setFilterValue,
    clearFilters,
    optionsByKey,
    filteredData,
    hasActiveFilters,
  } = useReportFilters(reports, filterConfigs);

  const mappedData = useMemo(
    () => mapReportData(filteredData ?? [], columns),
    [filteredData, columns]
  );

  return (
    <Row gutter={[10, 10]}>
      <Col span={24}>
        <Button
          onClick={() => loadReports()}
          loading={isLoading}
          disabled={isLoading}
          type="primary"
        >
          {t('generateCsvReport')}
        </Button>
      </Col>
      <Col span={24}>
        <ReportFilterBar
          configs={filterConfigs}
          values={values}
          optionsByKey={optionsByKey}
          onChange={setFilterValue}
          onClear={clearFilters}
          hasActiveFilters={hasActiveFilters}
          counts={
            reports
              ? {
                  shown: filteredData?.length ?? 0,
                  total: reports.length,
                }
              : undefined
          }
        />
      </Col>
      <Col span={24}>
        <ReportDownloads
          fileName={fileName}
          headers={headers}
          data={mappedData}
        />
      </Col>
    </Row>
  );
};

export default IneligibleReports;
